import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Clock, Users, UserCheck, UserX, AlertTriangle, 
  TrendingUp, Wallet, Calendar, ArrowRight, IndianRupee,
  CheckCircle, XCircle, CalendarCheck
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useBusiness } from "@/contexts/BusinessContext";
import MobileAttendanceA from "@/components/mobile/MobileAttendanceA";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [todayStatus, setTodayStatus] = useState(null);
  const [salaryStatus, setSalaryStatus] = useState(null);
  const [userRole, setUserRole] = useState("");

  // Mobile viewport detection — all roles get the Variation A mobile design.
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchTodayStatus();
    fetchUserRole();
    fetchMySalary();
  }, [selectedBusinessId]);

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchMySalary = async () => {
    try {
      // First try current month
      let response = await fetch(`${BACKEND_URL}/api/attendance/my-salary`, {
        credentials: "include"
      });
      
      if (response.ok) {
        let data = await response.json();
        
        // If no slip for current month, try previous month
        if (!data.has_slip) {
          const now = new Date();
          const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
          const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
          
          response = await fetch(`${BACKEND_URL}/api/attendance/my-salary?month=${prevMonth}&year=${prevYear}`, {
            credentials: "include"
          });
          
          if (response.ok) {
            data = await response.json();
          }
        }
        
        setSalaryStatus(data);
      }
    } catch (error) {
      console.error("Error fetching salary status:", error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/dashboard?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/dashboard`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/today`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTodayStatus(data);
      }
    } catch (error) {
      console.error("Error fetching today status:", error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // ════════ Mobile-native attendance home ════════
  // All roles on <768px → Variation A · Vibrant mobile design. The mobile
  // component fetches its own status/stats so it doesn't wait on this page's
  // loading state.
  if (isMobileViewport) {
    return <MobileAttendanceA userRole={userRole} />;
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="attendance-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Clock className="w-8 h-8 text-emerald-600" />
            Attendance Dashboard
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {dashboard?.today?.date ? formatDate(dashboard.today.date) : "Today"}
          </p>
        </div>
        
        <Button 
          onClick={() => navigate("/attendance/punch")}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="quick-punch-btn"
        >
          <Clock className="w-4 h-4 mr-2" />
          {todayStatus?.punched_in && !todayStatus?.punched_out ? "Punch Out" : "Punch In"}
        </Button>
      </div>

      {/* My Status Card */}
      {todayStatus && (
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">My Status Today</p>
                <p className="text-2xl font-bold mt-1">
                  {todayStatus.punched_out 
                    ? "Day Complete" 
                    : todayStatus.punched_in 
                      ? "Working" 
                      : "Not Punched In"}
                </p>
                {todayStatus.record?.punch_in_time && (
                  <p className="text-emerald-100 text-sm mt-2">
                    Punch In: {new Date(todayStatus.record.punch_in_time).toLocaleTimeString()}
                    {todayStatus.record?.punch_out_time && (
                      <> | Punch Out: {new Date(todayStatus.record.punch_out_time).toLocaleTimeString()}</>
                    )}
                  </p>
                )}
              </div>
              <div className="text-right">
                {todayStatus.record?.working_hours > 0 && (
                  <div>
                    <p className="text-emerald-100 text-sm">Hours Worked</p>
                    <p className="text-3xl font-bold">{todayStatus.record.working_hours.toFixed(1)}h</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Salary Status Card - For Staff */}
      {salaryStatus && salaryStatus.has_slip && (
        <Card className={`border-0 ${
          salaryStatus.payment_status === "paid" 
            ? "bg-gradient-to-r from-green-500 to-emerald-600" 
            : "bg-gradient-to-r from-orange-500 to-amber-600"
        } text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  My Salary - {new Date(0, salaryStatus.month - 1).toLocaleString('en', {month: 'long'})} {salaryStatus.year}
                </p>
                <p className="text-3xl font-bold mt-2 flex items-center">
                  <IndianRupee className="w-7 h-7" />
                  {salaryStatus.net_payable?.toLocaleString("en-IN") || 0}
                </p>
                <div className="mt-3 flex items-center gap-3">
                  {salaryStatus.payment_status === "paid" ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Paid
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                      <XCircle className="w-4 h-4" />
                      Unpaid
                    </span>
                  )}
                  {salaryStatus.salary_credit_date && (
                    <span className="inline-flex items-center gap-1 text-sm text-white/80">
                      <CalendarCheck className="w-4 h-4" />
                      Credited: {new Date(salaryStatus.salary_credit_date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric"
                      })}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  {salaryStatus.payment_status === "paid" ? (
                    <CheckCircle className="w-8 h-8" />
                  ) : (
                    <Clock className="w-8 h-8" />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Staff</p>
                <p className="text-xl font-bold text-slate-800 dark:text-white">
                  {dashboard?.today?.total_staff || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Present</p>
                <p className="text-xl font-bold text-green-600">
                  {dashboard?.today?.present || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Absent</p>
                <p className="text-xl font-bold text-red-600">
                  {dashboard?.today?.absent || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Late Today</p>
                <p className="text-xl font-bold text-amber-600">
                  {dashboard?.today?.late || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Stats & Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Monthly Overview */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-emerald-600" />
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-slate-600 dark:text-slate-300">Total Late Entries</span>
              </div>
              <span className="font-bold text-amber-600">{dashboard?.month?.total_late || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-slate-600 dark:text-slate-300">Total Overtime Hours</span>
              </div>
              <span className="font-bold text-emerald-600">{dashboard?.month?.total_overtime_hours || 0}h</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-slate-600 dark:text-slate-300">Pending Salary Slips</span>
              </div>
              <span className="font-bold text-blue-600">{dashboard?.pending_salary_slips || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate("/attendance/records")}
            >
              <span>View Attendance Records</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate("/attendance/staff")}
            >
              <span>Manage Staff Profiles</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate("/attendance/salary")}
            >
              <span>Salary Management</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => navigate("/attendance/reports")}
            >
              <span>Generate Reports</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AttendanceDashboard;
